import { useNavigate } from "react-router-dom";
import { DELIVERABLE_STATUS_META, formatDate, relativeDue } from "@/lib/constants";
import { DeliverableBadge } from "@/components/Badges";
import { EditorBubble } from "@/components/EditorBubble";
import { ClientAvatar } from "@/components/ClientAvatar";
import { MapPin, CalendarClock, UserCog, History } from "lucide-react";
import { parseISO, isBefore, startOfDay } from "date-fns";

const COLUMNS = [
  { key: "NEW", label: "New", accent: "#94A3B8" },
  { key: "EDITING", label: "Editing", accent: "#A855F7" },
  { key: "READY", label: "Ready", accent: "#F59E0B" },
  { key: "DELIVERED", label: "Delivered", accent: "#10B981" },
];

function ProductCard({ item, onDragStart, onClick }) {
  const overdue = item.due_date && item.status !== "DELIVERED" && isBefore(parseISO(item.due_date), startOfDay(new Date()));
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, item)}
      onClick={onClick}
      data-testid={`product-card-${item.deliverable_id}`}
      className={`group cursor-pointer rounded-lg border bg-card p-3 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 ${item.status === "DELIVERED" ? "opacity-70" : ""} ${overdue ? "border-rose-500/40" : "border-border"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <DeliverableBadge type={item.type} />
        <div className="flex items-center gap-1.5">
          {item.is_reshoot && (
            <span title="Reshoot — property has multiple shoots" data-testid={`reshoot-dot-${item.deliverable_id}`}
              className="inline-flex items-center gap-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
              <History className="h-2.5 w-2.5" /> Reshoot
            </span>
          )}
          <span className="font-mono text-[10px] text-muted-foreground">#{item.order_number}</span>
        </div>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-xs font-medium leading-snug">
        <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
        <span className="line-clamp-2">{item.address}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {item.client_name && <ClientAvatar name={item.client_name} className="h-4 w-4 text-[7px]" />}<span className="truncate">{item.client_name || "—"}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
        <span title={item.due_date ? formatDate(item.due_date) : "No due date"}
          className={`flex items-center gap-1 text-sm font-semibold ${overdue ? "text-rose-500 dark:text-rose-400" : "text-foreground"}`}>
          <CalendarClock className="h-3.5 w-3.5" />{item.due_date ? relativeDue(item.due_date) : "No due date"}
        </span>
        {item.editor_name && (
          <EditorBubble name={item.editor_name} color={item.editor_color} link={item.editor_link}
            className="px-1.5 py-0.5 text-[10px]" iconClass="h-2.5 w-2.5" />
        )}
      </div>
    </div>
  );
}

export default function ProductBoard({ orders, onMove }) {
  const navigate = useNavigate();

  const items = [];
  for (const o of orders) {
    if (o.shoot_cancelled) continue;
    for (const d of (o.deliverables || [])) {
      if (d.due_source === "excluded") continue;
      items.push({
        order_id: o.id,
        order_number: o.number,
        address: o.address || o.title,
        client_name: o.client_name,
        deliverable_id: d.id || d.aryeo_item_id,
        type: d.type,
        title: d.title,
        status: d.status,
        due_date: d.due_date,
        is_reshoot: o.is_reshoot,
        editor_name: d.editor_name,
        editor_color: d.editor_color,
        editor_link: d.editor_link,
      });
    }
  }

  const grouped = COLUMNS.reduce((acc, c) => {
    acc[c.key] = items.filter((i) => i.status === c.key).sort((a, b) => (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1);
    return acc;
  }, {});

  const onDragStart = (e, item) => {
    e.dataTransfer.setData("orderId", item.order_id);
    e.dataTransfer.setData("deliverableId", item.deliverable_id);
    e.dataTransfer.setData("from", item.status);
  };

  const onDrop = (e, status) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData("orderId");
    const deliverableId = e.dataTransfer.getData("deliverableId");
    const from = e.dataTransfer.getData("from");
    if (orderId && deliverableId && from !== status) onMove(orderId, deliverableId, status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="product-board">
      {COLUMNS.map((col) => {
        const list = grouped[col.key];
        return (
          <div key={col.key}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, col.key)}
            data-testid={`product-column-${col.key}`}
            className="flex w-[320px] shrink-0 flex-col rounded-xl border border-border bg-card/30">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.accent }} />
                <span className="text-sm font-semibold">{col.label}</span>
              </div>
              <span className="font-mono text-xs text-muted-foreground">{list.length}</span>
            </div>
            <div className="flex-1 space-y-2.5 overflow-y-auto px-3 pb-3" style={{ maxHeight: "calc(100vh - 240px)" }}>
              {list.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border py-8 text-center text-xs text-muted-foreground">Drop products here</div>
              ) : (
                list.map((it) => (
                  <ProductCard key={it.deliverable_id} item={it}
                    onDragStart={onDragStart}
                    onClick={() => navigate(`/orders/${it.order_id}`)} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
